import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'
import type {
  AdminUserCreateRequest,
  AdminUserResponse,
  AdminUserUpdateRequest,
  AuditLogEntry,
  NoBarcodeVariant,
  ShopifySyncSetting,
} from '@/lib/api/session11Types'

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'] as const,
    queryFn: () => apiFetch<AdminUserResponse[]>('GET', '/api/admin/users'),
    staleTime: 30_000,
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AdminUserCreateRequest) =>
      apiFetch<AdminUserResponse>('POST', '/api/admin/users', { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function usePatchUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: AdminUserUpdateRequest }) =>
      apiFetch<AdminUserResponse>('PATCH', `/api/admin/users/${userId}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useAuditLog(params: { username?: string; action?: string; limit?: number }) {
  return useQuery({
    queryKey: ['audit-log', params] as const,
    queryFn: () =>
      apiFetch<AuditLogEntry[]>('GET', '/api/admin/audit-log', {
        query: {
          username: params.username || undefined,
          action: params.action || undefined,
          limit: params.limit ?? 100,
        },
      }),
    // Always pull fresh on open — an audit log that shows stale entries is
    // worse than a brief spinner. Recent PO actions (created/approved) must be
    // visible the moment the admin opens this view.
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useNoBarcodeVariants() {
  return useQuery({
    queryKey: ['no-barcode'] as const,
    queryFn: () => apiFetch<NoBarcodeVariant[]>('GET', '/api/inventory/no-barcode'),
    staleTime: 60_000,
  })
}

export function useShopifySyncSetting() {
  return useQuery({
    queryKey: ['shopify-sync-setting'] as const,
    queryFn: () => apiFetch<ShopifySyncSetting>('GET', '/api/settings/shopify-sync'),
    staleTime: 30_000,
  })
}

export function useSetShopifySyncSetting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enabled: boolean) =>
      apiFetch<ShopifySyncSetting>('PUT', '/api/settings/shopify-sync', { body: { enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shopify-sync-setting'] }),
  })
}

// "Check history" dashboard flag thresholds (K, N, M). Locally typed
// until the next `npm run gen-api` regen, like the admin types below.
export interface LifetimeSpikeSettings {
  ratio_min: number
  units_60d_max: number
  min_days: number
  // N_product (L1b): 60-day units cap for the product-level flag
  product_units_60d_max: number
}

// Admin -> Data Health "Listings not pooled across options" (L1b): products
// whose non-size options (Color, Style ...) split their product-level
// lifetime figures into several pools. Locally typed, as above.
export interface UnpooledListing {
  product_id: number
  product_title: string | null
  vendor: string | null
  option_names: string[]
  pools: number
}

export function useUnpooledListings() {
  return useQuery({
    queryKey: ['unpooled-listings'] as const,
    queryFn: () => apiFetch<UnpooledListing[]>('GET', '/api/inventory/unpooled-listings'),
    staleTime: 60_000,
  })
}

// Rec Qty rounding threshold X (brief R1). Locally typed, as above.
export interface RecQtyRoundingSettings {
  threshold: number
}

export function useRecQtyRoundingSettings() {
  return useQuery({
    queryKey: ['rec-qty-rounding-settings'] as const,
    queryFn: () => apiFetch<RecQtyRoundingSettings>('GET', '/api/settings/rec-qty-rounding'),
    staleTime: 30_000,
  })
}

export function useSetRecQtyRoundingSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RecQtyRoundingSettings) =>
      apiFetch<RecQtyRoundingSettings>('PUT', '/api/settings/rec-qty-rounding', { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rec-qty-rounding-settings'] })
      // The backend rebuilds the dashboard with the new X; refetch so the
      // grid picks up the rebuild instead of its 5-minute cache.
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useLifetimeSpikeSettings() {
  return useQuery({
    queryKey: ['lifetime-spike-settings'] as const,
    queryFn: () => apiFetch<LifetimeSpikeSettings>('GET', '/api/settings/lifetime-spike'),
    staleTime: 30_000,
  })
}

export function useSetLifetimeSpikeSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LifetimeSpikeSettings) =>
      apiFetch<LifetimeSpikeSettings>('PUT', '/api/settings/lifetime-spike', { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lifetime-spike-settings'] })
      // The backend rebuilds the dashboard with the new thresholds; refetch
      // so the grid picks up the rebuild instead of its 5-minute cache.
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Manual inventory adjustment (admin)
// Locally-typed because the OpenAPI generated types haven't been regenerated
// since the new endpoints were added. When `npm run gen-api` runs next, these
// can move into session11Types alongside the others.
// ---------------------------------------------------------------------------

export interface VariantSearchResult {
  variant_id: number
  product_id: number
  product_title: string
  variant_title: string
  sku: string
  barcode: string
  vendor: string
}

export interface VariantInventoryAtLocation {
  location_id: number | null
  location_name: string
  available: number
  incoming: number
  on_hand: number
}

export interface VariantInventoryResponse {
  variant_id: number
  inventory_item_id: string | null
  locations: VariantInventoryAtLocation[]
}

export interface InventoryAdjustRequest {
  variant_id: number
  location_id: number
  available?: number | null
  incoming?: number | null
  on_hand?: number | null
}

export interface QuantityAdjustResult {
  name: 'available' | 'incoming' | 'on_hand'
  before: number
  after: number
  delta: number
  ok: boolean
  error?: string | null
}

export interface InventoryAdjustResponse {
  variant_id: number
  location_id: number
  results: QuantityAdjustResult[]
}

export function useInventorySearch(q: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['inventory-search', q] as const,
    queryFn: () =>
      apiFetch<VariantSearchResult[]>('GET', '/api/admin/inventory-search', {
        query: { q, limit: 20 },
      }),
    // Skip the query while the user is typing fewer than 2 characters —
    // saves a round-trip and avoids huge result sets for single letters.
    enabled: enabled && q.trim().length >= 2,
    staleTime: 10_000,
  })
}

export function useVariantInventory(variantId: number | null) {
  return useQuery({
    queryKey: ['variant-inventory', variantId] as const,
    queryFn: () =>
      apiFetch<VariantInventoryResponse>('GET', '/api/admin/inventory-current', {
        query: { variant_id: variantId ?? 0 },
      }),
    enabled: variantId !== null && variantId > 0,
    // Always refetch on focus — the user may have edited Shopify directly
    // in another tab; we want the most current "before" values.
    staleTime: 0,
  })
}

export function useAdjustInventory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: InventoryAdjustRequest) =>
      apiFetch<InventoryAdjustResponse>('POST', '/api/admin/inventory-adjust', { body }),
    onSuccess: (_data, vars) => {
      // Refresh the variant detail so the form shows the new "after" values.
      qc.invalidateQueries({ queryKey: ['variant-inventory', vars.variant_id] })
    },
  })
}
